import { Component, HostListener, ChangeDetectorRef, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from '../../shared/services/api.service';
import { ProvidersCacheService } from '../../shared/services/providers-cache.service';
import { AllProvidersCacheService } from '../../shared/services/all-providers-cache.service';
import { AgeLimitsCacheService } from '../../shared/services/age-limits-cache.service';
import { SlotsCacheService } from '../../shared/services/slots-cache.service';
import { BenchService } from '../../shared/services/bench.service';
import { SheetLoggerService } from '../../shared/services/sheet-logger.service';
import { AppointmentsDataService } from '../../shared/services/appointments-data.service';
import { AuthContextService, PatientInfo } from '../../shared/auth-context.service';
import { is65PlusScreeningDisabled } from '../../../environments/environment';
import { Navbar } from '../../navbar/navbar';
import { Inputfiled } from '../../inputfiled/inputfiled';
import { Calendar } from '../../calendar/calendar';
import { PhoneNumber } from '../../shared/phone-number/phone-number';
import { PhoneInput } from '../../shared/phone-input/phone-input';
import { ClickableLink } from '../../shared/clickable-link/clickable-link';
import { getStateOptions, getAllowedVisitTypeCodesForState, mapVisitTypeCodeToLabel, getAllStateNames, STATE_VISIT_CONFIG, VISIT_TYPE_LABEL_BY_CODE } from '../../shared/data/state-visit-config';
import { Footer } from '../../footer/footer';
import { Loader } from '../../shared/loader/loader';
import { finalize } from 'rxjs/operators';
import { CarrierOption, InsuranceCoverageService, NO_INSURANCE_CARD_LABEL, SELF_PAY_CARRIER_CODE, SELF_PAY_FINANCIAL_CLASS_ID } from '../../shared/services/insurance-coverage.service';
import { AppointmentSupportNeeded } from '../../shared/appointment-support-needed/appointment-support-needed';
import { carrierVisibleInState } from '../../shared/data/carriers-by-code-and-state';
import { orderCarriersBySearch } from '../../shared/utils/carrier-search';
import { hasHopeNewPatientHandoffParams, parseHopeNewPatientPrefill, stripHopeHandoffParamsFromUrl } from '../../shared/utils/hope-handoff';
import { PartnerProviderContextService } from '../../shared/services/partner-provider-context.service';
import { VariantUserInfoService } from '../../shared/services/variant-user-info.service';
import { UserActivityService } from '../../shared/services/user-activity.service';
interface DropdownOption {
  value: string;
  label: string;
}

interface LookupPatientResponse {
  exists: boolean;
  usePatientPortalMessage?: boolean;
  message?: string | null;
}

@Component({
  selector: 'app-new-patient',
  imports: [CommonModule, FormsModule, Navbar, Inputfiled, Calendar, PhoneNumber, PhoneInput, ClickableLink, Footer, Loader, AppointmentSupportNeeded],
  templateUrl: './new-patient.html',
  styleUrl: './new-patient.css'
})
export class NewPatient implements OnInit {
  constructor(private router: Router, private auth: AuthContextService, private api: ApiService, private cdr: ChangeDetectorRef, private ngZone: NgZone, private sheetLogger: SheetLoggerService, private appointmentsData: AppointmentsDataService, private providersCache: ProvidersCacheService, private allProvidersCache: AllProvidersCacheService, private ageLimits: AgeLimitsCacheService, private slotsCache: SlotsCacheService, private bench: BenchService, private insuranceCoverage: InsuranceCoverageService, private partnerProviderContext: PartnerProviderContextService, private variantUserInfo: VariantUserInfoService, private userActivity: UserActivityService) {}

  private pushDataLayerEvent(eventName: string, payload: Record<string, unknown> = {}): void {
    const normalizedEventName = String(eventName || '').trim();
    if (!normalizedEventName) return;
    try {
      const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
      dataLayer.push({
        event: normalizedEventName,
        ...payload
      });
    } catch {}
  }

  private trackTreatmentTypeClick(type: string): void {
    if (type === this.treatmentTypes.medication) {
      this.pushDataLayerEvent('ols_intake_for_medication_management');
      return;
    }
    if (type === this.treatmentTypes.psychotherapy) {
      this.pushDataLayerEvent('ols_intake_for_therapy');
    }
  }

  private readonly stateSelectionEventMap: Record<string, string> = {
    AZ: 'ols_arizona_selected',
    AR: 'ols_arkansas_selected',
    GA: 'ols_georgia_selected',
    MO: 'ols_missouri_selected',
    NY: 'ols_new_york_selected',
    NC: 'ols_north_carolina_selected',
    OK: 'ols_oklahoma_selected',
    TN: 'ols_tennessee_selected'
  };

  private trackStateSelection(stateCode: string): void {
    const eventName = this.stateSelectionEventMap[String(stateCode || '').trim().toUpperCase()];
    if (eventName) {
      this.pushDataLayerEvent(eventName);
    }
  }

  trackSeeAvailableSlotsClick(): void {
    this.pushDataLayerEvent('ols_see_available_slots_click');
    this.userActivity.trackClick('new_patient_see_available_slots_button_clicked', 'button', 'See Available Slots');
  }

  closeSupportNeededModal(): void {
    this.showSupportNeededModal = false;
  }

  trackOlderAdultsFormShown(): void {
    this.pushDataLayerEvent('ols_older_adults_form_shown');
  }

  trackOlderAdultsSeeAvailableSlotsClick(): void {
    this.pushDataLayerEvent('ols_older_adults_see_available_slots_click');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-wrapper')) {
      this.closeAllDropdowns();
    }
  }
  
 
  formData = {
    firstName: '',
    lastName: '',
    birthDate: '',
    birthSex: '',
    email: '',
    phoneNumber: '',
    state: '',
    treatmentType: ''
  };

  payType: 'insurance' | 'self-pay' | '' = '';
  providerCarrier: string = '';
  providerCarrierId: number | string | null = null;
  providerFinancialClassId: string | null = 'fclass12';
  carriers: CarrierOption[] = [];
  carriersLoaded = false;
  carrierDropdownOpen = false;
  carrierSearch = '';
  noInsuranceCardWithMe: boolean = false;

 
  isCheckingExistingAccount = false;
  showSupportNeededModal = false;

 
  showInPersonModal: boolean = false;
  inPerson = { firstName: '', lastName: '', phone: '', email: '', state: '', consent: false };
  modalErrors = { firstName: '', lastName: '', phone: '', email: '', state: '', consent: '' };
  isSubmittingInPerson: boolean = false;
  showInPersonSuccessToast: boolean = false;
  show65PlusModal: boolean = false;
  show65PlusUncertainModal: boolean = false;
  screening65Plus = {
    cognitiveDiagnosis: null as boolean | null,
    medicationDependence: null as boolean | null,
    poaProxy: null as boolean | null,
    technologyAssist: null as boolean | null
  };
  private pendingProceedAfter65Plus: (() => void) | null = null;
  private bypass65PlusGateOnce: boolean = false;
  private olderAdultsScreeningForSheet: {
    cognitive_diagnosis: string;
    medication_independence: string;
    clinical_decision: string;
    technology_independence: string;
  } | null = null;
  private toastDurationMs: number = 3500;

  // Form validation errors
  formErrors = {
    firstName: '',
    lastName: '',
    birthDate: '',
    birthSex: '',
    email: '',
    phoneNumber: '',
    state: '',
    treatmentType: '',
    coverage: ''
  };

  // Inline error banner state for existing-account detection
  showExistingAccountBanner = false;
  existingAccountMessage = 'An account with these details already exists. Please log in to the Patient Portal to manage your appointments or update your information.';
  existingAccountBannerUsePlainText = false;
  inlineBannerMessage: string = '';
  checkingAvailability = false;
  canSeeSlots = false;
  isLoadingSlots = false;
  private slotsRefreshTimer: any = null;
  private lastActivityTime = Date.now();
  private readonly SLOTS_REFRESH_INTERVAL = 5 * 60 * 1000;

  // Dropdown state
  birthSexDropdownOpen = false;
  stateDropdownOpen = false;
  stateSearch = '';

  // Treatment type options
  treatmentTypes = {
    medication: 'medication',
    psychotherapy: 'psychotherapy'
  };

  treatmentLabelByType: Record<string, { heading: string; desc: string }> = {
    medication: {
      heading: 'Intake for Medication Management',
      desc: 'For diagnosing conditions and managing prescriptions or refills.'
    },
    psychotherapy: {
      heading: 'Intake for Therapy/Counseling',
      desc: 'For talking through emotions, experiences, and coping strategies.'
    }
  };

  // State treatment availability
  statesTreatmentData: { [key: string]: string[] } = {
   
    'NC': ['medication', 'psychotherapy'],
    'GA': ['medication', 'psychotherapy'],
    'TN': ['medication' ],
    "NY": ['medication'],
    'AZ': ['medication'], 
    'AR': ['medication'], 
    // 'OR': ['medication'], 
    // 'WA': ['medication'], 
    'MO': ['medication'], 
    'OK': ['medication'],
  };

  // Dropdown options
  birthSexOptions: DropdownOption[] = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  stateOptions: DropdownOption[] = getStateOptions();
  // Restrict in-person modal state dropdown to NC only
  get modalStateOptions(): DropdownOption[] {
    try { return this.stateOptions.filter(s => s.value === 'NC'); } catch { return []; }
  }

  isStateOptionDisabled(option: DropdownOption): boolean {
    return !this.partnerProviderContext.isStateActiveForPartner(option.value);
  }

  get sortedStateOptions(): DropdownOption[] {
    return [...this.stateOptions].sort((a, b) => Number(this.isStateOptionDisabled(a)) - Number(this.isStateOptionDisabled(b)));
  }

  get filteredStateOptions(): DropdownOption[] {
    const term = (this.stateSearch || '').trim().toLowerCase();
    const options = this.sortedStateOptions;
    if (!term) return options;
    return options
      .filter((opt) => {
        const label = (opt.label || '').toLowerCase();
        const value = (opt.value || '').toLowerCase();
        return label.includes(term) || value.startsWith(term);
      })
      .sort((a, b) => {
        const aLabel = (a.label || '').toLowerCase();
        const bLabel = (b.label || '').toLowerCase();
        const aValue = (a.value || '').toLowerCase();
        const bValue = (b.value || '').toLowerCase();
        const aRank = aLabel === term || aValue === term ? 0 : (aLabel.startsWith(term) || aValue.startsWith(term) ? 1 : 2);
        const bRank = bLabel === term || bValue === term ? 0 : (bLabel.startsWith(term) || bValue.startsWith(term) ? 1 : 2);
        if (aRank !== bRank) return aRank - bRank;
        return Number(this.isStateOptionDisabled(a)) - Number(this.isStateOptionDisabled(b));
      });
  }

  get patientStateForCarriers(): string {
    return (this.formData.state || '').toString().trim().toUpperCase();
  }

  // The carrier list is built from every state the eligible providers work in, so keep only the
  // carriers listed for the patient's state (e.g. NC patients must not get MOCAID / ARBCBS).
  private get carriersForPatientState(): CarrierOption[] {
    const stateCode = this.toStateAbbrev(this.formData.state);
    return this.carriers.filter((carrier) => carrierVisibleInState(carrier.states, stateCode));
  }

  get sortedCarriers(): CarrierOption[] {
    const list = this.carriersForPatientState
      .filter((carrier) => carrier.code !== 'SP' && !carrier.specialCoverage);

    return [...list].sort((a, b) => {
      const aSpecial = !!a.specialCoverage;
      const bSpecial = !!b.specialCoverage;
      if (aSpecial !== bSpecial) return aSpecial ? 1 : -1;
      const aLabel = (a.label || '').toUpperCase();
      const bLabel = (b.label || '').toUpperCase();
      const aOther = a.code === 'OTHER' || aLabel === 'OTHER' || aLabel === 'OTHERS';
      const bOther = b.code === 'OTHER' || bLabel === 'OTHER' || bLabel === 'OTHERS';
      if (aOther !== bOther) return aOther ? 1 : -1;
      return (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase());
    });
  }

  get filteredCarriers(): CarrierOption[] {
    return orderCarriersBySearch(this.sortedCarriers, this.carrierSearch);
  }

  get carrierDropdownEmptyMessage(): string {
    return this.hasCarrierPrerequisites() ? 'No carriers found' : 'Please enter DOB and select state first';
  }

  private hasCarrierPrerequisites(): boolean {
    return !!(
      this.formData.state &&
      this.formData.birthDate &&
      this.formData.birthDate.trim().length >= 8
    );
  }

  

  private handleNcPsychSlotsGate(onProceed: () => void): void {
    const stateLabel = (this.getStateLabel() || this.formData.state || '').toString();
    const visitUi = (mapVisitTypeCodeToLabel(this.formData.treatmentType) || 'Psychotherapy');
    const isNc = (stateLabel || '').toLowerCase() === 'north carolina' || (this.formData.state || '').toUpperCase() === 'NC';
    const isPsy = (visitUi || '').toLowerCase().startsWith('psycho');
    if (!(isNc && isPsy)) {
      onProceed();
      return;
    }
    this.providersCache.ensureForStateAndVisit('NC', 'Psychotherapy').then(async () => {
      const ids = await this.partnerProviderContext.filterProfileIds(
        this.providersCache.getProfileIds('NC', 'Psychotherapy'),
        'NC',
        'Psychotherapy'
      );
      if (!Array.isArray(ids) || ids.length === 0) {
        if (this.partnerProviderContext.hasActivePartner()) {
          this.showSupportNeededModal = true;
          this.canSeeSlots = false;
          this.isSubmitting = false;
          this.cdr.detectChanges();
          return;
        }
        try { sessionStorage.setItem('flow.wl.entry', '1'); } catch {}
        this.router.navigate(['/waiting-list']);
        return;
      }
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const payload: any = { state: 'NC', visitType: 'Psychotherapist', startDate: `${y}-${m}-${d}`, startTime: `${hh}:${mm}`, profileIds: ids };
      const benchStart = Date.now();
      this.api.dispatch<any>('/providers/slots', payload).subscribe({
        next: (resp) => {
          try {
            const responseWeekDate = this.getSlotsResponseWeekDate(resp, payload.startDate);
            this.bench.sendSlotsBench(payload.startDate, responseWeekDate, Math.max(0, Date.now() - benchStart));
          } catch {}
          try {
            let total = 0;
            const cols = Array.isArray(resp?.columnprofiles) ? resp.columnprofiles : [];
            for (const col of cols) {
              const days = Array.isArray(col?.timeslotdays) ? col.timeslotdays : [];
              for (const day of days) {
                const slots = Array.isArray(day?.timeslots) ? day.timeslots : [];
                total += slots.length;
              }
            }
            if (total > 0) {
              try { sessionStorage.setItem('flow.canSeeAvailableSlots', '1'); } catch {}
              onProceed();
            } else {
              try { sessionStorage.setItem('flow.wl.entry', '1'); } catch {}
              this.router.navigate(['/waiting-list']);
            }
          } catch {
            try { sessionStorage.setItem('flow.wl.entry', '1'); } catch {}
            this.router.navigate(['/waiting-list']);
          }
        },
        error: () => {
          try { sessionStorage.setItem('flow.wl.entry', '1'); } catch {}
          this.router.navigate(['/waiting-list']);
        }
      });
    });
  }

  getExistingAccountBannerHtml(): string {
    const rawMessage = (this.existingAccountMessage || '').trim();
    if (!rawMessage) return '';

    // The backend should return plain text, but we've seen some environments return
    // an HTML (or HTML-escaped) anchor tag for the phone number. This banner renders
    // via [innerHTML], so normalize the message to safe text and inject our own
    // clickable tel: link.
    const normalized = this.escapeHtml(this.stripPhoneAnchorMarkup(rawMessage));

    return normalized.replace(
      /\b(?:\+?1\s*)?\(?919\)?[-\s]*391[-\s]*7202\b/g,
      '<a href="tel:9193917202" class="existing-account-phone-link">919-391-7202</a>'
    );
  }

  private stripPhoneAnchorMarkup(message: string): string {
    // Remove real anchor tags.
    let out = message.replace(/<\/?a\b[^>]*>/gi, '');

    // Remove HTML-escaped anchor tags (e.g., "&lt;a href='tel:+1919...'&gt;").
    out = out
      .replace(/&lt;\s*\/?\s*a\b[^&]*&gt;/gi, '')
      .replace(/&#60;\s*\/?\s*a\b[^&]*&#62;/gi, '');

    return out;
  }

  private escapeHtml(message: string): string {
    return (message || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private getSlotsResponseWeekDate(resp: any, fallbackStartDate: string): string {
    try {
      const cols = Array.isArray(resp?.columnprofiles) ? resp.columnprofiles : [];
      let best: string = '';
      for (const col of cols) {
        const days = Array.isArray(col?.timeslotdays) ? col.timeslotdays : [];
        for (const day of days) {
          const raw = String(day?.startdate || '').trim();
          if (!raw) continue;
          const dateOnly = raw.split('T')[0] || raw;
          if (!best || dateOnly < best) best = dateOnly;
        }
      }
      return best || fallbackStartDate;
    } catch {
      return fallbackStartDate;
    }
  }

  // Custom dropdown methods
  toggleBirthSexDropdown() {
    this.birthSexDropdownOpen = !this.birthSexDropdownOpen;
    if (this.birthSexDropdownOpen) {
      this.closeStateDropdown();
      this.userActivity.trackClick('new_patient_birth_sex_clicked', 'input', 'Birth Sex');
    }
  }

  onBirthSexTriggerKeydown(event: KeyboardEvent) {
    const key = event.key;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.toggleBirthSexDropdown();
    } else if (key === 'Escape') {
      if (this.birthSexDropdownOpen) {
        event.preventDefault();
        this.birthSexDropdownOpen = false;
      }
    }
  }

  openStateDropdown(): void {
    const wasOpen = this.stateDropdownOpen;
    this.stateDropdownOpen = true;
    this.birthSexDropdownOpen = false;
    this.carrierDropdownOpen = false;
    if (!wasOpen) this.stateSearch = '';
    setTimeout(() => {
      const input = document.getElementById('stateSearchInput') as HTMLInputElement | null;
      input?.focus();
    }, 0);
  }

  closeStateDropdown(): void {
    this.stateDropdownOpen = false;
    this.stateSearch = '';
  }

  onStateSearchChange(value: string): void {
    this.stateSearch = value || '';
    this.stateDropdownOpen = true;
    this.clearFieldError('state');
    this.showExistingAccountBanner = false;
    const term = this.stateSearch.trim().toLowerCase();
    if (!term) return;
    const exactMatch = this.filteredStateOptions.find((opt) =>
      (opt.label || '').toLowerCase() === term && !this.isStateOptionDisabled(opt)
    );
    if (exactMatch) {
      this.selectState(exactMatch);
    }
  }

  selectFirstStateMatch(event?: Event): void {
    if (event) event.preventDefault();
    const option = this.filteredStateOptions.find((opt) => !this.isStateOptionDisabled(opt));
    if (option) this.selectState(option);
  }

  openCarrierDropdown(): void {
    this.carrierDropdownOpen = true;
    this.birthSexDropdownOpen = false;
    this.closeStateDropdown();
    this.carrierSearch = this.providerCarrier || this.carrierSearch || '';
    if (this.hasCarrierPrerequisites() && this.filteredCarriers.length === 0) {
      void this.refreshCarrierOptionsForCurrentDetails();
    }
  }

  onCarrierSearchChange(value: string): void {
    this.carrierSearch = value || '';
    this.carrierDropdownOpen = true;
    if ((this.carrierSearch || '').trim()) {
      this.payType = 'insurance';
      this.noInsuranceCardWithMe = false;
    }
    this.clearFieldError('coverage');
    this.showExistingAccountBanner = false;
    const exactMatch = this.filteredCarriers.find((carrier) => (carrier.label || '').toLowerCase() === (this.carrierSearch || '').trim().toLowerCase());
    if (exactMatch) {
      this.selectCarrier(exactMatch);
      return;
    }
    this.providerCarrier = '';
    this.providerCarrierId = null;
    this.providerFinancialClassId = 'fclass12';
  }

  selectCarrier(carrier: CarrierOption): void {
    this.providerCarrier = carrier.label || '';
    this.providerCarrierId = carrier.id ?? null;
    this.providerFinancialClassId = carrier.financialClassId || 'fclass12';
    this.carrierSearch = this.providerCarrier;
    this.payType = carrier.specialCoverage === 'self-pay' ? 'self-pay' : 'insurance';
    this.noInsuranceCardWithMe = false;
    this.carrierDropdownOpen = false;
    this.clearFieldError('coverage');
    this.showExistingAccountBanner = false;
    this.syncInsuranceInfoToAuth();
    void this.refreshAvailabilityAfterCoverageChange();
  }

  selectFirstCarrierMatch(event?: Event): void {
    if (event) event.preventDefault();
    const carrier = this.filteredCarriers[0];
    if (carrier) this.selectCarrier(carrier);
  }

  onSelfPayChange(checked: boolean): void {
    if (checked) {
      this.pushDataLayerEvent('ols_1st_self_pay_button_click');
      this.noInsuranceCardWithMe = false;
    }
    this.payType = checked ? 'self-pay' : '';
    if (checked) {
      this.providerCarrier = '';
      this.providerCarrierId = null;
      this.providerFinancialClassId = 'fclass12';
      this.carrierSearch = '';
      this.carrierDropdownOpen = false;
    }
    this.clearFieldError('coverage');
    this.showExistingAccountBanner = false;
    this.syncInsuranceInfoToAuth();
    void this.refreshAvailabilityAfterCoverageChange();
  }

  onNoInsuranceCardChange(checked: boolean): void {
    this.noInsuranceCardWithMe = checked;
    if (checked) {
      this.pushDataLayerEvent('ols_1st_no_insurance_button_click');
      const specialCarrier = this.carriers.find((carrier) => carrier.specialCoverage === 'insurance-info-unavailable');
      this.providerCarrier = specialCarrier?.label || NO_INSURANCE_CARD_LABEL;
      this.providerCarrierId = specialCarrier?.id ?? SELF_PAY_CARRIER_CODE;
      this.providerFinancialClassId = specialCarrier?.financialClassId || SELF_PAY_FINANCIAL_CLASS_ID;
      this.carrierSearch = '';
      this.carrierDropdownOpen = false;
    }
    this.payType = checked ? 'insurance' : '';
    if (!checked) {
      this.providerCarrier = '';
      this.providerCarrierId = null;
      this.providerFinancialClassId = 'fclass12';
    }
    this.clearFieldError('coverage');
    this.showExistingAccountBanner = false;
    this.syncInsuranceInfoToAuth();
    void this.refreshAvailabilityAfterCoverageChange();
  }

  // Clear specific field errors when user starts typing
  clearFieldError(fieldName: keyof typeof this.formErrors): void {
    if (this.formErrors[fieldName]) {
      this.formErrors[fieldName] = '';
    }
  }

  // Update input change handlers to clear errors
  onFirstNameChange(): void {
    this.clearFieldError('firstName');
    this.showExistingAccountBanner = false;
  }

  onLastNameChange(): void {
    this.clearFieldError('lastName');
    this.showExistingAccountBanner = false;
  }

  async onBirthDateChange(value?: string): Promise<void> {
    if (typeof value === 'string') {
      this.formData.birthDate = value;
    }
    this.clearFieldError('birthDate');
    this.showExistingAccountBanner = false;
    try {
      this.auth.updatePatientInfo({ dateOfBirth: this.formData.birthDate });
    } catch {}
    
    if (this.formData.birthDate && this.formData.birthDate.trim().length >= 8) {
      try {
        await this.ageLimits.initialize();
        const allStates = getAllStateNames();
        const allVisitTypes = ['Medication Management', 'Psychotherapy'];
        this.providersCache.buildGlobalAgeFilteredCache(
          this.formData.birthDate,
          this.ageLimits,
          allStates,
          allVisitTypes
        );
      } catch {}
    }
    await this.refreshCarrierOptionsForCurrentDetails();
    
    await this.maybeCheckAvailability();
    if (this.canSeeSlots && this.formData.state) {
      this.fetchStateSlots(this.formData.state);
    }
  }

  onEmailChange(): void {
    this.clearFieldError('email');
    this.showExistingAccountBanner = false;
  }

  selectBirthSex(option: DropdownOption) {
    this.formData.birthSex = option.value;
    this.birthSexDropdownOpen = false;
    this.clearFieldError('birthSex');
    this.showExistingAccountBanner = false;
  }

  selectState(option: DropdownOption) {
    if (this.isStateOptionDisabled(option)) return;
    this.formData.state = option.value;
    this.trackStateSelection(option.value);
    this.userActivity.trackClick('new_patient_state_selected', 'select', option.label);
    this.stateSearch = option.label;
    this.stateDropdownOpen = false;
    this.clearFieldError('state');
    this.showExistingAccountBanner = false;
    
    this.formData.treatmentType = '';
    this.carriers = [];
    void this.refreshCarrierOptionsForCurrentDetails();
    this.syncCarrierSelectionForState();
    this.syncInsuranceInfoToAuth();
    
    void this.maybeCheckAvailability();

    const hasDob = !!(this.formData.birthDate && this.formData.birthDate.trim().length >= 8);
    if (hasDob) {
      this.fetchStateSlots(this.formData.state);
    }
  }

  // Treatment type methods
  getAvailableTreatments(): string[] {
    if (!this.formData.state) return [];
    return getAllowedVisitTypeCodesForState(this.formData.state);
  }

  hasMultipleTreatmentOptions(): boolean {
    return this.getAvailableTreatments().length > 1;
  }

  hasSingleTreatmentOption(): boolean {
    return this.getAvailableTreatments().length === 1;
  }

  shouldShowTreatmentSection(): boolean {
    return !!this.formData.state && this.getAvailableTreatments().length > 0;
  }

  isTreatmentTypeDisabled(type: string): boolean {
    if (!this.formData.state) return false;
    const label = mapVisitTypeCodeToLabel(type);
    if (!label) return false;
    return !this.partnerProviderContext.isVisitTypeAvailableForPartnerState(this.formData.state, label);
  }

  onTreatmentTypeOptionClick(type: string): void {
    if (this.isTreatmentTypeDisabled(type)) return;
    this.trackTreatmentTypeClick(type);
    if (type === this.treatmentTypes.medication) {
      this.userActivity.trackClick('new_patient_intake_for_medication_management_selected', 'radio', this.getTreatmentHeading(type));
    } else if (type === this.treatmentTypes.psychotherapy) {
      this.userActivity.trackClick('new_patient_intake_for_therapy_counseling_selected', 'radio', this.getTreatmentHeading(type));
    }
    void this.selectTreatmentType(type);
  }

  async selectTreatmentType(type: string): Promise<void> {
    this.formData.treatmentType = type;
    this.clearFieldError('treatmentType');
    this.showExistingAccountBanner = false;
    await this.refreshCarrierOptionsForCurrentDetails();
    await this.maybeCheckAvailability();
    if (this.canSeeSlots && this.formData.state) {
      this.fetchStateSlots(this.formData.state);
    }
  }

  getTreatmentHeading(type: string): string {
    return this.treatmentLabelByType[type]?.heading || '';
  }

  getTreatmentDesc(type: string): string {
    return this.treatmentLabelByType[type]?.desc || '';
  }

  getBirthSexLabel(): string {
    const selected = this.birthSexOptions.find(option => option.value === this.formData.birthSex);
    return selected ? selected.label : '';
  }

  getStateLabel(): string {
    const selected = this.stateOptions.find(option => option.value === this.formData.state);
    return selected ? selected.label : '';
  }

  closeAllDropdowns() {
    this.birthSexDropdownOpen = false;
    this.closeStateDropdown();
    this.carrierDropdownOpen = false;
  }

  // Global keyboard shortcuts
  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const tag = (target?.tagName || '').toLowerCase();
    const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select';
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (isTyping) return;

    const key = (event.key || '').toLowerCase();

    // 1) Birth Sex quick select: f → female, m → male, p → prefer-not-to-say when dropdown open
    const isBirthSexFocused = (target?.id === 'birthSexTrigger') || (typeof (target as any)?.closest === 'function' && !!(target as any).closest('#birthSexTrigger')) || (document?.activeElement as any)?.id === 'birthSexTrigger';
    if (this.birthSexDropdownOpen || isBirthSexFocused) {
      if (key === 'f') { this.selectBirthSex({ value: 'female', label: 'Female' }); return; }
      if (key === 'm') { this.selectBirthSex({ value: 'male', label: 'Male' }); return; }
      if (key === 'p') { this.selectBirthSex({ value: 'prefer-not-to-say', label: 'Prefer not to say' }); return; }
    }

  }

  ngOnInit(): void {
    try { this.userActivity.trackPageView(); } catch {}

    this.loadCarriersFromApiOrStorage();

    this.ageLimits.initialize().then(() => {
      this.cdr.detectChanges();
    }).catch((err) => {
    });

    this.setupActivityTracking();
    this.startSlotsRefreshTimer();

    if (hasHopeNewPatientHandoffParams()) {
      this.applyHopeHandoffPrefill();
    } else {
      try {
        const info: PatientInfo | null = this.auth.getPatientInfo();
        if (info) {
          this.formData.firstName = (info.legalFirstName || '').toString();
          this.formData.lastName = (info.legalLastName || '').toString();
          this.formData.email = (info.emailAddress || '').toString();
          this.formData.phoneNumber = (info.phoneNumber || '').toString();
          this.formData.birthDate = (info.dateOfBirth || '').toString();
          const g = (info.gender || '').toString();
          this.formData.birthSex = g.toUpperCase().startsWith('M') ? 'male' : (g.toUpperCase().startsWith('F') ? 'female' : (g ? 'prefer-not-to-say' : ''));
          const stateVal = (info.state || '').toString();
          const opt = this.stateOptions.find(o => o.value === stateVal || o.label === stateVal);
          this.formData.state = opt ? opt.value : stateVal;
          if (!this.formData.treatmentType && info.visitType) {
            const vt = (info.visitType || '').toLowerCase();
            this.formData.treatmentType = vt.includes('medication') ? this.treatmentTypes.medication : (vt.includes('psych') ? this.treatmentTypes.psychotherapy : '');
          }
          if (info.insurance) {
            this.payType = (info.insurance.payType as any) || '';
            this.providerCarrier = (info.insurance.providerCarrier || '').toString();
            this.providerCarrierId = info.insurance.providerCarrierId ?? null;
            this.providerFinancialClassId = info.insurance.primaryFinancialClassId ?? 'fclass12';
            this.carrierSearch = this.providerCarrier || '';
            this.noInsuranceCardWithMe = this.payType === 'insurance' && String(this.providerCarrierId || '').toUpperCase() === SELF_PAY_CARRIER_CODE;
          }
          this.birthSexDropdownOpen = false;
          this.stateDropdownOpen = false;
          this.carrierDropdownOpen = false;
          this.syncCarrierSelectionForState();
          this.cdr.detectChanges();

          if (this.formData.birthDate && this.formData.birthDate.trim().length >= 8) {
            this.ageLimits.initialize().then(() => {
              const allStates = getAllStateNames();
              const allVisitTypes = ['Medication Management', 'Psychotherapy'];
              this.providersCache.buildGlobalAgeFilteredCache(
                this.formData.birthDate,
                this.ageLimits,
                allStates,
                allVisitTypes
              );
            }).catch(() => {});
          }
          void this.refreshCarrierOptionsForCurrentDetails();

          if (this.hasAllAvailabilityInputs()) {
            void this.partnerProviderContext.ensureLoaded().then(() => this.maybeCheckAvailability());
          }
        }
      } catch {}
    }

    try {
      const nav = this.router.getCurrentNavigation();
      const msg = (nav?.extras?.state as any)?.bannerMessage;
      if (msg) { this.inlineBannerMessage = msg; }
    } catch {}
  }

  private applyHopeHandoffPrefill(): void {
    try {
      if (!hasHopeNewPatientHandoffParams()) return;

      try { this.auth.clearAll(); } catch {}

      this.formData = {
        firstName: '',
        lastName: '',
        birthDate: '',
        birthSex: '',
        email: '',
        phoneNumber: '',
        state: '',
        treatmentType: ''
      };
      this.payType = '';
      this.providerCarrier = '';
      this.providerCarrierId = null;
      this.providerFinancialClassId = 'fclass12';
      this.carrierSearch = '';
      this.noInsuranceCardWithMe = false;
      this.canSeeSlots = false;
      this.inlineBannerMessage = '';
      this.checkingAvailability = false;

      const prefill = parseHopeNewPatientPrefill() || {};
      if (prefill.firstName) this.formData.firstName = prefill.firstName;
      if (prefill.lastName) this.formData.lastName = prefill.lastName;
      if (prefill.email) this.formData.email = prefill.email;
      if (prefill.phoneNumber) this.formData.phoneNumber = prefill.phoneNumber;
      if (prefill.birthDate) this.formData.birthDate = prefill.birthDate;
      if (prefill.birthSex) this.formData.birthSex = prefill.birthSex;
      if (prefill.state) {
        const opt = this.stateOptions.find(o => o.value === prefill.state || o.label === prefill.state);
        this.formData.state = opt ? opt.value : prefill.state;
      }
      if (prefill.treatmentType) this.formData.treatmentType = prefill.treatmentType;

      try {
        const nextInfo: PatientInfo = {};
        if (this.formData.firstName) nextInfo.legalFirstName = this.formData.firstName;
        if (this.formData.lastName) nextInfo.legalLastName = this.formData.lastName;
        if (this.formData.email) nextInfo.emailAddress = this.formData.email;
        if (this.formData.phoneNumber) nextInfo.phoneNumber = this.formData.phoneNumber;
        if (this.formData.birthDate) nextInfo.dateOfBirth = this.formData.birthDate;
        if (this.formData.birthSex) nextInfo.gender = this.formData.birthSex;
        if (this.formData.state) nextInfo.state = this.formData.state;
        if (this.formData.treatmentType) nextInfo.visitType = mapVisitTypeCodeToLabel(this.formData.treatmentType);
        this.auth.setPatientInfo(nextInfo);
      } catch {}

      this.birthSexDropdownOpen = false;
      this.stateDropdownOpen = false;
      this.carrierDropdownOpen = false;
      this.syncCarrierSelectionForState();
      this.cdr.detectChanges();

      if (this.formData.birthDate && this.formData.birthDate.trim().length >= 8) {
        this.ageLimits.initialize().then(() => {
          const allStates = getAllStateNames();
          const allVisitTypes = ['Medication Management', 'Psychotherapy'];
          this.providersCache.buildGlobalAgeFilteredCache(
            this.formData.birthDate,
            this.ageLimits,
            allStates,
            allVisitTypes
          );
        }).catch(() => {});
      }
      void this.refreshCarrierOptionsForCurrentDetails();
      if (this.hasAllAvailabilityInputs()) {
        void this.partnerProviderContext.ensureLoaded().then(() => this.maybeCheckAvailability());
      }

      stripHopeHandoffParamsFromUrl();
    } catch {}
  }

  private hasAllAvailabilityInputs(): boolean {
    const hasDob = !!(this.formData.birthDate && this.formData.birthDate.trim().length >= 8);
    const hasState = !!this.formData.state;
    const hasVisit = !!this.formData.treatmentType;
    const hasCoverage = this.insuranceCoverage.hasCoverageSelection(this.getCoverageSelection());
    return hasDob && hasState && hasVisit && hasCoverage;
  }

  private async maybeCheckAvailability(): Promise<void> {
    try {
      if (!this.hasAllAvailabilityInputs()) {
        this.checkingAvailability = false;
        this.canSeeSlots = false;
        this.inlineBannerMessage = '';
        this.cdr.detectChanges();
        return;
      }
      this.checkingAvailability = true;
      this.cdr.detectChanges();
      const uiVisit = mapVisitTypeCodeToLabel(this.formData.treatmentType);
      await this.providersCache.ensureForStateAndVisit(this.formData.state, uiVisit);
      const ids = this.providersCache.getProfileIds(this.formData.state, uiVisit);
      const ageEligible = this.ageLimits.filterProfileIdsByAge(ids, this.formData.birthDate, uiVisit) || [];
      const eligible = await this.insuranceCoverage.filterProfileIdsBySelection(ageEligible, this.getCoverageSelection(), this.formData.state);
      const partnerEligible = await this.partnerProviderContext.filterProfileIds(eligible, this.formData.state, uiVisit);
      let ageOk = false;
      try {
        const raw = (this.formData.birthDate || '').toString().trim().replace(/\./g, '/').replace(/-/g, '/');
        const parts = raw.split('/');
        if (parts.length === 3) {
          const mm = Number(parts[0]);
          const dd = Number(parts[1]);
          const yyyy = Number(parts[2]);
          if (mm && dd && yyyy) {
            const dob = new Date(yyyy, mm - 1, dd);
            if (isFinite(dob.getTime())) {
              const now = new Date();
              let age = now.getFullYear() - dob.getFullYear();
              const m = now.getMonth() - dob.getMonth();
              if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
              ageOk = age >= 4;
            }
          }
        }
      } catch {
        ageOk = false;
      }
      const ok = Array.isArray(partnerEligible) && partnerEligible.length > 0 && ageOk;
      this.canSeeSlots = ok;
      if (ok) {
        this.inlineBannerMessage = '';
        this.loadSlotsFromCache();
      } else {
        this.inlineBannerMessage = '';
      }
    } catch (error) {
      this.canSeeSlots = false;
      this.inlineBannerMessage = '';
    } finally {
      this.checkingAvailability = false;
      this.cdr.detectChanges();
    }
  }

  private async fetchStateSlots(stateCode: string): Promise<void> {
    if (!stateCode) return;
    
    const normalizedStateCode = this.toStateAbbrev(stateCode);
    const visitTypes = getAllowedVisitTypeCodesForState(normalizedStateCode);
    if (visitTypes.length === 0) return;

    const startDate = this.getStateTodayDateString(normalizedStateCode);
    const startTime = this.getCurrentTimeInStateTz(normalizedStateCode);
    const cacheScope = this.getSlotsCacheScope(this.formData.birthDate || this.auth.getPatientInfo()?.dateOfBirth || '');
    
    const fetchPromises = visitTypes.map(async (visitTypeCode) => {
      const visitType = mapVisitTypeCodeToLabel(visitTypeCode);
      try {
        await this.providersCache.ensureForStateAndVisit(normalizedStateCode, visitType);
        const profileIds = this.providersCache.getProfileIds(normalizedStateCode, visitType);
        if (!Array.isArray(profileIds) || profileIds.length === 0) return;

        let eligibleIds: number[] | undefined = profileIds;
        try {
          const dob = this.formData.birthDate || '';
          const filtered = this.ageLimits.filterProfileIdsByAge(profileIds, dob, visitType);
          if (Array.isArray(filtered) && filtered.length > 0) {
            eligibleIds = filtered;
          } else {
            eligibleIds = [];
          }
        } catch {
          eligibleIds = profileIds;
        }

        if (!eligibleIds || eligibleIds.length === 0) return;
        eligibleIds = await this.insuranceCoverage.filterProfileIdsBySelection(eligibleIds, this.getCoverageSelection(), normalizedStateCode);
        if (!eligibleIds || eligibleIds.length === 0) return;
        eligibleIds = await this.partnerProviderContext.filterProfileIds(eligibleIds, normalizedStateCode, visitType);
        if (!eligibleIds || eligibleIds.length === 0) return;

        const existing = this.slotsCache.get(normalizedStateCode, startDate, visitType, cacheScope);
        if (existing) {
          try {
            const cols = Array.isArray(existing?.columnprofiles) ? existing.columnprofiles : [];
            let total = 0;
            for (const c of cols) {
              const days = Array.isArray(c?.timeslotdays) ? c.timeslotdays : [];
              for (const d of days) {
                const ts = Array.isArray(d?.timeslots) ? d.timeslots : [];
                total += ts.length;
              }
            }
            if (total > 0) {
              return;
            }
          } catch {}
        }

        if (this.slotsCache.isFetching(normalizedStateCode, startDate, visitType, cacheScope)) {
          return;
        }

        const payload: any = {
          state: normalizedStateCode,
          visitType: visitType,
          startDate: startDate,
          startTime: startTime,
          profileIds: eligibleIds
        };

        return new Promise<void>((resolve) => {
          try { this.slotsCache.beginFetch(normalizedStateCode, startDate, visitType, cacheScope); } catch {}
          const benchStart = Date.now();
          this.api.dispatch<any>('/providers/slots', payload).subscribe({
            next: (resp) => {
              try {
                const responseWeekDate = this.getSlotsResponseWeekDate(resp, startDate);
                this.bench.sendSlotsBench(startDate, responseWeekDate, Math.max(0, Date.now() - benchStart));
              } catch {}
              try {
                this.slotsCache.set(normalizedStateCode, startDate, visitType, resp, cacheScope);
                try {
                  const cols = Array.isArray(resp?.columnprofiles) ? resp.columnprofiles : [];
                  let total = 0;
                  for (const c of cols) {
                    const days = Array.isArray(c?.timeslotdays) ? c.timeslotdays : [];
                    for (const d of days) {
                      const ts = Array.isArray(d?.timeslots) ? d.timeslots : [];
                      total += ts.length;
                    }
                  }
                } catch {}
              } catch (err) {
              }
              try { this.slotsCache.endFetch(normalizedStateCode, startDate, visitType, cacheScope); } catch {}
              resolve();
            },
            error: (err) => {
              try { this.slotsCache.endFetch(normalizedStateCode, startDate, visitType, cacheScope); } catch {}
              resolve();
            }
          });
        });
      } catch (err) {
        return Promise.resolve();
      }
    });

    await Promise.all(fetchPromises);
  }

  private getTimeZoneForState(stateCode: string): string {
    const tzMap: Record<string, string> = {
      'NC': 'America/New_York',
      'NY': 'America/New_York',
      'FL': 'America/New_York',
      'GA': 'America/New_York',
      'TN': 'America/Chicago',
      'MO': 'America/Chicago',
      'AR': 'America/Chicago',
      'IL': 'America/Chicago',
      'TX': 'America/Chicago',
      'OK': 'America/Chicago',
      'AZ': 'America/Phoenix',
      'WA': 'America/Los_Angeles',
      'CA': 'America/Los_Angeles',
      'OR': 'America/Los_Angeles'
    };
    return tzMap[stateCode.toUpperCase()] || 'America/New_York';
  }

  private getStateTodayDateString(stateCode: string): string {
    try {
      const tz = this.getTimeZoneForState(stateCode);
      const todayStr = new Intl.DateTimeFormat('en-CA', { 
        timeZone: tz, 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }).format(new Date());
      return todayStr;
    } catch {
      const t = new Date();
      const y = t.getFullYear();
      const m = String(t.getMonth() + 1).padStart(2, '0');
      const d = String(t.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  private getCurrentTimeInStateTz(stateCode: string): string {
    try {
      const tz = this.getTimeZoneForState(stateCode);
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const parts = fmt.formatToParts(now);
      const hour = parts.find(p => p.type === 'hour')?.value || '00';
      const minute = parts.find(p => p.type === 'minute')?.value || '00';
      return `${hour}:${minute}`;
    } catch {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }
  }

  private loadSlotsFromCache(): void {
    if (!this.formData.state || !this.formData.treatmentType) return;
    
    const visitType = mapVisitTypeCodeToLabel(this.formData.treatmentType);
    const stateCode = this.toStateAbbrev(this.formData.state);
    const startDate = this.getStateTodayDateString(stateCode);
    const cacheScope = this.getSlotsCacheScope(this.formData.birthDate || this.auth.getPatientInfo()?.dateOfBirth || '');
    
    const cached = this.slotsCache.get(stateCode, startDate, visitType, cacheScope);
  }

  private setupActivityTracking(): void {
    try {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.addEventListener(event, () => {
          this.lastActivityTime = Date.now();
          this.resetSlotsRefreshTimer();
        }, { passive: true });
      });
    } catch {}
  }

  private startSlotsRefreshTimer(): void {
    this.resetSlotsRefreshTimer();
  }

  private resetSlotsRefreshTimer(): void {
    try {
      if (this.slotsRefreshTimer) {
        clearTimeout(this.slotsRefreshTimer);
      }
      
      this.slotsRefreshTimer = setTimeout(() => {
        this.checkAndRefreshSlots();
      }, this.SLOTS_REFRESH_INTERVAL);
    } catch {}
  }

  private async checkAndRefreshSlots(): Promise<void> {
    const inactiveTime = Date.now() - this.lastActivityTime;
    
    if (inactiveTime >= this.SLOTS_REFRESH_INTERVAL && this.formData.state) {
      // Previously refreshed slots here; now rely on AvailableSlots page for fetching
      this.lastActivityTime = Date.now();
      this.resetSlotsRefreshTimer();
    } else {
      this.resetSlotsRefreshTimer();
    }
  }

  onPhoneValueChange(): void {
    this.clearFieldError('phoneNumber');
    this.showExistingAccountBanner = false;
  }

  onPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    // Clear phone error when user starts typing
    this.clearFieldError('phoneNumber');
    this.showExistingAccountBanner = false;
    
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Format the phone number as user types
    let formatted = '';
    if (cleaned.length > 0) {
      if (cleaned.length <= 3) {
        formatted = cleaned;
      } else if (cleaned.length <= 6) {
        formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      } else {
        formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
    }
    
    // Update the form data and input value
    this.formData.phoneNumber = formatted;
    target.value = formatted;
  }

  onPhoneKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        // Allow: home, end, left, right
        (event.keyCode >= 35 && event.keyCode <= 39)) {
      return;
    }
    
    // Ensure that it is a number and stop the keypress if not
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  onPrevious() {
    this.userActivity.trackClick('new_patient_previous_button_clicked', 'button', 'Previous');
    // Navigate back to patient type selection
    this.router.navigate(['/scheduler']);
  }

  onPhoneNumberClicked(): void {
    this.userActivity.trackClick('new_patient_phone_number_clicked', 'link', 'Phone Number');
  }
  onClickHereClicked(): void {
    this.userActivity.trackClick('new_patient_click_here_clicked', 'link', 'Click here.');
    this.openInPersonModal();
  }
  onPhoneNumberFieldBlurred(): void {
    this.trackFieldEntered('new_patient_phone_number_field_entered', 'input', 'Phone Number', this.formData.phoneNumber);
  }
  trackFieldEntered(fieldKey: string, fieldType: string, fieldLabel: string, value: string | null | undefined): void {
    const strValue = (value || '').toString();
    this.userActivity.trackFieldInput(fieldKey, fieldType, fieldLabel, strValue.length > 0, strValue.length, strValue);
  }

  // In-person modal handlers
  openInPersonModal() { this.showInPersonModal = true; }
  closeInPersonModal() { this.showInPersonModal = false; }
  formatInPersonPhone(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 10);
    const formatted = digits.length <= 3 ? digits : (digits.length <= 6 ? `${digits.slice(0,3)}-${digits.slice(3)}` : `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`);
    input.value = formatted;
    this.inPerson.phone = formatted;
  }
  private clearInPersonError(field: keyof typeof this.modalErrors) {
    if (this.modalErrors[field]) this.modalErrors[field] = '';
  }
  private validateInPerson(): boolean {
    let ok = true;
    // reset
    this.modalErrors = { firstName: '', lastName: '', phone: '', email: '', state: '', consent: '' };
    const nameMax = 20;
    const emailMax = 40;
    const email = (this.inPerson.email || '').trim();
    const first = (this.inPerson.firstName || '').trim();
    const last = (this.inPerson.lastName || '').trim();
    if (!first) { this.modalErrors.firstName = 'Please enter your legal first name'; ok = false; }
    else if (first.length > nameMax) { this.modalErrors.firstName = `Maximum ${nameMax} characters allowed`; ok = false; }
    if (!last) { this.modalErrors.lastName = 'Please enter your legal last name'; ok = false; }
    else if (last.length > nameMax) { this.modalErrors.lastName = `Maximum ${nameMax} characters allowed`; ok = false; }
    if (!email) { this.modalErrors.email = 'Please enter your email address'; ok = false; }
    else if (email.length > emailMax) { this.modalErrors.email = `Maximum ${emailMax} characters allowed`; ok = false; }
    else if (!this.isValidEmail(email)) { this.modalErrors.email = 'Please enter a valid email address'; ok = false; }
    const phone = (this.inPerson.phone || '').trim();
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    if (!phone) { this.modalErrors.phone = 'Please enter your phone number'; ok = false; }
    else if (!phoneRegex.test(phone)) { this.modalErrors.phone = 'Please enter a valid phone number'; ok = false; }
    if (!this.inPerson.state) { this.modalErrors.state = 'Please select your state of residence'; ok = false; }
    if (!this.inPerson.consent) { this.modalErrors.consent = 'Please consent to receive text messages to continue'; ok = false; }
    return ok;
  }
  private resetInPersonForm(): void {
    this.inPerson = { firstName: '', lastName: '', phone: '', email: '', state: '', consent: false };
    this.modalErrors = { firstName: '', lastName: '', phone: '', email: '', state: '', consent: '' };
  }
  submitInPerson() {
    if (!this.validateInPerson()) return;
    this.isSubmittingInPerson = true;
    try {
      let hadError = false;
      this.sheetLogger
        .logInPersonLead({
          firstName: (this.inPerson.firstName || '').trim(),
          lastName: (this.inPerson.lastName || '').trim(),
          phone: (this.inPerson.phone || '').trim(),
          email: (this.inPerson.email || '').trim(),
          state: (this.inPerson.state || '').toString()
        })
        .pipe(
          finalize(() => {
            this.ngZone.run(() => {
              // Always stop the loader at stream end
              this.isSubmittingInPerson = false;
              this.cdr.detectChanges();
            });
          })
        )
        .subscribe({
          next: () => {
            this.ngZone.run(() => {
              // Close modal immediately on success and redirect to success page
              try {
                setTimeout(() => {
                  this.resetInPersonForm();
                  this.showInPersonModal = false;
                  this.cdr.detectChanges();
                }, 0);
              } catch {}
              this.router.navigate(['/inperson/success']);
            });
          },
          error: () => {
            this.ngZone.run(() => {
              // Keep modal open so user can retry
              hadError = true;
              this.cdr.detectChanges();
            });
          }
        });
    } catch {
      this.ngZone.run(() => {
        this.isSubmittingInPerson = false;
        // Keep modal open so user can retry
      });
    }
  }

  get screening65PlusCanProceed(): boolean {
    if (this.screening65Plus.cognitiveDiagnosis !== false) return false;
    if (this.screening65Plus.medicationDependence !== false) return false;
    if (this.screening65Plus.poaProxy !== false) return false;
    return this.screening65Plus.technologyAssist !== null;
  }

  close65PlusModal(): void {
    this.show65PlusModal = false;
    this.pendingProceedAfter65Plus = null;
    this.reset65PlusScreeningAnswers();
  }

  private toScreeningYesNo(value: boolean | null): string {
    if (value === null) return '';
    return value ? 'Yes' : 'No';
  }

  private captureOlderAdultsScreeningForSheet(): void {
    const s = this.screening65Plus;
    if (
      s.cognitiveDiagnosis === null &&
      s.medicationDependence === null &&
      s.poaProxy === null &&
      s.technologyAssist === null
    ) {
      return;
    }
    this.olderAdultsScreeningForSheet = {
      cognitive_diagnosis: this.toScreeningYesNo(s.cognitiveDiagnosis),
      medication_independence: this.toScreeningYesNo(s.medicationDependence),
      clinical_decision: this.toScreeningYesNo(s.poaProxy),
      technology_independence: this.toScreeningYesNo(s.technologyAssist),
    };
  }

  private getOlderAdultsScreeningForSheet() {
    return this.olderAdultsScreeningForSheet ? { ...this.olderAdultsScreeningForSheet } : undefined;
  }

  private clearOlderAdultsScreeningForSheet(): void {
    this.olderAdultsScreeningForSheet = null;
  }

  private resolveOlderAdultsScreeningForSheetLog(): {
    screening?: {
      cognitive_diagnosis: string;
      medication_independence: string;
      clinical_decision: string;
      technology_independence: string;
    };
    applicable: boolean;
  } {
    const applicable = this.shouldShow65PlusScreening();
    if (!applicable) {
      this.clearOlderAdultsScreeningForSheet();
      this.sheetLogger.clearOlderAdultsScreeningFromCache();
      return {
        applicable: false,
        screening: this.sheetLogger.getEmptyOlderAdultsScreening()
      };
    }
    const captured = this.getOlderAdultsScreeningForSheet();
    if (captured) {
      return { applicable: true, screening: captured };
    }
    return { applicable: true };
  }

  private buildSheetScreeningPartial(): Record<string, any> {
    const resolved = this.resolveOlderAdultsScreeningForSheetLog();
    if (!resolved.applicable) {
      return {
        ...this.sheetLogger.getEmptyOlderAdultsScreening(),
        screening_questions_applicable: false
      };
    }
    if (!resolved.screening) return {};
    return {
      ...resolved.screening,
      screening_questions_applicable: true
    };
  }

  on65PlusProceed(): void {
    if (!this.screening65PlusCanProceed) return;
    this.show65PlusModal = false;
    if (this.screening65Plus.technologyAssist === true) {
      this.show65PlusUncertainModal = true;
      return;
    }
    this.captureOlderAdultsScreeningForSheet();
    const next = this.pendingProceedAfter65Plus;
    this.pendingProceedAfter65Plus = null;
    this.reset65PlusScreeningAnswers();
    if (next) next();
  }

  on65PlusUncertainClose(): void {
    this.show65PlusUncertainModal = false;
    this.captureOlderAdultsScreeningForSheet();
    const next = this.pendingProceedAfter65Plus;
    this.pendingProceedAfter65Plus = null;
    this.reset65PlusScreeningAnswers();
    if (next) next();
  }

  private shouldShow65PlusScreening(): boolean {
    if (is65PlusScreeningDisabled()) return false;
    const age = this.computeAgeForScope(this.formData.birthDate || '');
    return age !== null && age >= 65;
  }

  private reset65PlusScreeningAnswers(): void {
    this.screening65Plus = {
      cognitiveDiagnosis: null,
      medicationDependence: null,
      poaProxy: null,
      technologyAssist: null
    };
  }

  isSubmitting = false;

  async onSubmit(): Promise<void> {
    if (this.isCheckingExistingAccount || this.isSubmitting) {
      return;
    }
    
    this.isSubmitting = true;
   
    try {
      const viteEnv: any = (typeof import.meta !== 'undefined' ? (import.meta as any).env : {}) || {};
      const hasKey = !!(viteEnv?.VITE_APP_ENCRYPTION_KEY || viteEnv?.NG_APP_ENCRYPTION_KEY || (window as any)?.VITE_APP_ENCRYPTION_KEY || (window as any)?.APP_ENCRYPTION_KEY);
     
    } catch {}
    
    this.clearAllErrors();
    
    // Validate form and show errors
    if (this.validateForm()) {
      const bypass65GateForThisSubmit = this.bypass65PlusGateOnce;
      this.bypass65PlusGateOnce = false;

      // Read the last-checked fingerprint and last-inserted sheet flag
      const prevKey = (typeof localStorage !== 'undefined' && localStorage.getItem('np.fingerprint')) || '';
      const fingerprint = [
        this.formData.firstName.trim(),
        this.formData.lastName.trim(),
        this.formData.birthDate,
        this.formData.birthSex,
        this.formData.email.trim(),
        this.formData.phoneNumber.trim(),
        this.formData.state,
        this.formData.treatmentType
      ].join('|');
      const identityKey = [
        this.formData.firstName.trim(),
        this.formData.lastName.trim(),
        this.formData.birthDate,
        this.formData.birthSex
      ].join('|');
      const prevIdentityKey = (typeof localStorage !== 'undefined' && localStorage.getItem('np.identityKey')) || '';

      // Persist base patient info to shared context (localStorage)
      const patientInfo: PatientInfo = {
        appointmentFor: 'me',
        legalFirstName: this.formData.firstName.trim(),
        legalLastName: this.formData.lastName.trim(),
        dateOfBirth: this.formData.birthDate,
        gender: this.getBirthSexLabel() || this.formData.birthSex,
        emailAddress: this.formData.email.trim(),
        phoneNumber: this.formData.phoneNumber.trim(),
        state: this.getStateLabel() || this.formData.state,
        insurance: {
          payType: this.payType || undefined,
          providerCarrier: this.providerCarrier || undefined,
          providerCarrierId: this.providerCarrierId ?? undefined,
          primaryFinancialClassId: this.providerFinancialClassId ?? undefined
        }
      };
      this.auth.updatePatientInfo(patientInfo);

      // Optionally persist initial appointment type from treatment selection
      if (this.formData.treatmentType) {
        const mappedType = mapVisitTypeCodeToLabel(this.formData.treatmentType);
        if (mappedType) {
          this.auth.updatePatientInfo({ visitType: mappedType });
        }
      }

      const isNcPsych = ((this.formData.state || '').toUpperCase() === 'NC' || (this.getStateLabel() || '').toLowerCase() === 'north carolina') && ((this.formData.treatmentType || '').toLowerCase() === 'psychotherapy' || mapVisitTypeCodeToLabel(this.formData.treatmentType) === 'Psychotherapy');

      let isNatasaTestAccount = false;
      try {
        const first = (this.formData.firstName || '').trim().toLowerCase();
        const last = (this.formData.lastName || '').trim().toLowerCase();
        const email = (this.formData.email || '').trim().toLowerCase();
        const dobNorm = (this.formData.birthDate || '').trim().replace(/\./g, '/').replace(/-/g, '/');
        isNatasaTestAccount = email === 'test@gmail.com' && (
          (first === 'test' && last === 'natasa') ||
          (first === 'natasa' && last === 'test')
        );
        if (isNatasaTestAccount) {
          try { sessionStorage.setItem('flow.bypass', '1'); } catch {}
        }
        const isBypassDob = dobNorm === '01/01/2000' || dobNorm === '2000/01/01';
        const isBypass = email === 'test@gmail.com' && isBypassDob && (
          first === 'test' && last === 'test'
        );
        if (isBypass) {
          try { sessionStorage.setItem('flow.bypass', '1'); } catch {}
          try { sessionStorage.setItem('flow.canSeeAvailableSlots', '1'); } catch {}
          this.isSubmitting = false;
          this.router.navigate(['/available-slots']);
          return;
        }
      } catch {}

      if (this.hasAllAvailabilityInputs()) {
        await this.maybeCheckAvailability();
      }

      if (isNatasaTestAccount) {
        try { sessionStorage.setItem('flow.canSeeAvailableSlots', '1'); } catch {}
        this.isSubmitting = false;
        this.router.navigate(['/available-slots']);
        return;
      }

      const identityChanged = identityKey !== prevIdentityKey;
      const formChanged = fingerprint !== prevKey;

      if (!this.canSeeSlots && this.hasAllAvailabilityInputs()) {
        this.show65PlusModal = false;
        this.show65PlusUncertainModal = false;
        this.pendingProceedAfter65Plus = null;
        this.showSupportNeededModal = true;
        this.isSubmitting = false;
        this.cdr.detectChanges();
        return;
      }

      if (this.shouldShow65PlusScreening() && !bypass65GateForThisSubmit) {
        if (this.canSeeSlots && this.formData.state) {
          this.fetchStateSlots(this.formData.state).catch(() => {});
        }
        this.pendingProceedAfter65Plus = () => {
          this.bypass65PlusGateOnce = true;
          void this.onSubmit();
        };
        this.show65PlusModal = true;
        this.trackOlderAdultsFormShown();
        this.isSubmitting = false;
        this.cdr.detectChanges();
        return;
      }

      const proceedToSlotsDirect = () => {
        try { sessionStorage.setItem('flow.canSeeAvailableSlots', '1'); } catch {}
        try {
          this.auth.updatePatientInfo({
            dateOfBirth: this.formData.birthDate,
            state: this.getStateLabel() || this.formData.state,
            insurance: {
              payType: this.payType || undefined,
              providerCarrier: this.providerCarrier || undefined,
              providerCarrierId: this.providerCarrierId ?? undefined,
              primaryFinancialClassId: this.providerFinancialClassId ?? undefined
            }
          });
          if (this.formData.treatmentType) {
            const mappedType = mapVisitTypeCodeToLabel(this.formData.treatmentType);
            if (mappedType) this.auth.updatePatientInfo({ visitType: mappedType });
          }
        } catch {}
        const startDate = this.getStateTodayDateString(this.toStateAbbrev(this.formData.state || this.auth.getPatientInfo()?.state || ''));
        try { this.saveDateToCache(startDate); } catch {}
        try {
          const uiVisit = mapVisitTypeCodeToLabel(this.formData.treatmentType || 'medication');
          const scope = this.getSlotsCacheScope(this.formData.birthDate || this.auth.getPatientInfo()?.dateOfBirth || '');
          const cached = this.slotsCache.get(this.toStateAbbrev(this.formData.state), startDate, uiVisit, scope);
        } catch {}
        try {
          this.variantUserInfo.syncUserInfo(this.formData.firstName, this.formData.lastName, this.formData.email);
        } catch {}
        try {
          this.sheetLogger.insertConcertGeneralReferral({
            firstName: this.formData.firstName.trim(),
            lastName: this.formData.lastName.trim(),
            phone: this.formData.phoneNumber.trim(),
            email: this.formData.email.trim(),
            dob: (this.formData.birthDate || '').replace(/-/g, '/'),
            state: this.getStateLabel() || this.formData.state,
            insurance: (this.payType === 'self-pay' || this.noInsuranceCardWithMe)
              ? ''
              : (this.providerCarrier || '').toString().trim()
          });
          this.sheetLogger.insertAprilHealthInitialIntake({
            firstName: this.formData.firstName.trim(),
            lastName: this.formData.lastName.trim(),
            phone: this.formData.phoneNumber.trim(),
            email: this.formData.email.trim(),
            dob: (this.formData.birthDate || '').replace(/-/g, '/'),
            insurance: (this.payType === 'self-pay' || this.noInsuranceCardWithMe)
              ? ''
              : (this.providerCarrier || '').toString().trim()
          });
          this.sheetLogger.insertFarReferral({
            firstName: this.formData.firstName.trim(),
            lastName: this.formData.lastName.trim(),
            phone: this.formData.phoneNumber.trim(),
            email: this.formData.email.trim(),
            dob: (this.formData.birthDate || '').replace(/-/g, '/'),
            insurance: (this.payType === 'self-pay' || this.noInsuranceCardWithMe)
              ? ''
              : (this.providerCarrier || '').toString().trim()
          });
        } catch {}
        this.isSubmitting = false;
        this.router.navigate(['/available-slots']);
      };

      const proceedToSlots = () => {
        proceedToSlotsDirect();
      };

      const logInitialIfNeeded = async () => {
        const dr = (typeof localStorage !== 'undefined' && localStorage.getItem('sheet.Date_received')) || '';
        const tr = (typeof localStorage !== 'undefined' && localStorage.getItem('sheet.Time_received')) || '';
        const row = (typeof localStorage !== 'undefined' && localStorage.getItem('sheet.RowNumber')) || '';
        const hasSheetKeys = !!(dr && tr && row);
        const resolved = this.resolveOlderAdultsScreeningForSheetLog();
        if (hasSheetKeys) {
          return;
        }
        try {
          // Fire-and-forget: must not delay proceedToSlots() below.
          const partnerFields = this.sheetLogger.getPartnerSheetPayload();
          this.appointmentsData.sendInitial({
            firstName: this.formData.firstName.trim(),
            lastName: this.formData.lastName.trim(),
            dob: (this.formData.birthDate || '').replace(/-/g, '/'),
            birthsex: this.getBirthSexLabel() || this.formData.birthSex,
            phone: this.formData.phoneNumber.trim(),
            email: this.formData.email.trim(),
            state: this.getStateLabel() || this.formData.state,
            partnerName: partnerFields?.['Partner_name'] || undefined,
            partnerType: partnerFields?.['partner_type'] || undefined
          });
        } catch {}
        try {
          await this.sheetLogger.logInitialPatientInfo({
            firstName: this.formData.firstName.trim(),
            lastName: this.formData.lastName.trim(),
            dob: (this.formData.birthDate || '').replace(/-/g, '/'),
            birthsex: this.getBirthSexLabel() || this.formData.birthSex,
            phone: this.formData.phoneNumber.trim(),
            email: this.formData.email.trim(),
            state: this.getStateLabel() || this.formData.state,
            olderAdultsScreening: resolved.screening,
            screeningQuestionsApplicable: resolved.applicable
          });
          this.clearOlderAdultsScreeningForSheet();
        } catch {}
      };
      const logSheetThenProceed = (
        partial?: Record<string, any>,
        onLogged?: () => void
      ) => {
        logInitialIfNeeded().then(() => {
          const mergedPartial = {
            ...this.buildSheetScreeningPartial(),
            ...(partial || {})
          };
          if (Object.keys(mergedPartial).length > 0) {
            this.sheetLogger.updateBasicInfoPartial(mergedPartial);
          }
          if (onLogged) onLogged();
        }).catch(() => {});
        proceedToSlots();
      };

      // Case A: nothing changed → skip checks and sheet insert, go straight to slots
        if (!formChanged) {
          if (isNcPsych) {
            this.handleNcPsychSlotsGate(() => { 
              logSheetThenProceed();
            });
            return;
          }
          logSheetThenProceed();
          return;
        }

      // Case B: identity (name/dob/gender) changed → re-check in AMD; if no account, insert/partial update minimal; then slots
      if (identityChanged) {
        const fullName = `${this.formData.firstName}`.trim() + (this.formData.lastName ? `, ${this.formData.lastName.trim()}` : '');
        const lookupPayload: any = {
          exactmatch: '1',
          name: fullName,
          dob: (this.formData.birthDate || '').replace(/-/g, '/'),
          gender: (this.formData.birthSex || '').toUpperCase().startsWith('M') ? 'M' : (this.formData.birthSex || '').toUpperCase().startsWith('F') ? 'F' : 'U'
        };
        this.showExistingAccountBanner = false;
        this.existingAccountBannerUsePlainText = false;
        this.isCheckingExistingAccount = true;
        this.api.dispatch<LookupPatientResponse | boolean>('/lookup-patient', lookupPayload).subscribe({
          next: (res) => {
            this.ngZone.run(() => {
              this.isCheckingExistingAccount = false;
              const obj = res !== null && typeof res === 'object' ? (res as LookupPatientResponse) : null;
              const existsFlag = obj
                ? !!obj.exists
                : (res === true) || (res as any === 'true') || (res as any === 1);
              // Only block when there is an actual restriction. A patient with only a free
              // (Meet & Greet) appointment returns exists:true but no message/portal flag —
              // that case should proceed to slots exactly like a new patient.
              const isBlocked = existsFlag && !!(obj?.message?.trim() || obj?.usePatientPortalMessage);
              if (isBlocked) {
                const apiMsg = (obj?.message || '').trim();
                if (apiMsg) {
                  this.existingAccountMessage = apiMsg;
                  this.existingAccountBannerUsePlainText = true;
                } else {
                  this.existingAccountMessage =
                    'An account with these details already exists. Please log in to the Patient Portal to manage your appointments or update your information.';
                  this.existingAccountBannerUsePlainText = false;
                }
                this.showExistingAccountBanner = true;
                this.isSubmitting = false;
                try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' as any }); } catch { window.scrollTo(0, 0); }
                this.cdr.detectChanges();
              } else {
                try { localStorage.setItem('np.identityKey', identityKey); } catch {}
                try { localStorage.setItem('np.fingerprint', fingerprint); } catch {}
                if (isNcPsych) {
                  this.handleNcPsychSlotsGate(() => {
                    logSheetThenProceed({
                      First_name: this.formData.firstName.trim(),
                      Last_name: this.formData.lastName.trim(),
                      DOB: (this.formData.birthDate || '').replace(/-/g, '/'),
                      Birthsex: (this.getBirthSexLabel() || this.formData.birthSex || '').toString()
                    });
                  });
                } else {
                  logSheetThenProceed({
                    First_name: this.formData.firstName.trim(),
                    Last_name: this.formData.lastName.trim(),
                    DOB: (this.formData.birthDate || '').replace(/-/g, '/'),
                    Birthsex: (this.getBirthSexLabel() || this.formData.birthSex || '').toString()
                  });
                }
              }
            });
          },
          error: () => {
            this.ngZone.run(() => {
              this.isCheckingExistingAccount = false;
              this.isSubmitting = false;
              this.cdr.detectChanges();
            });
          }
        });
        return;
      }

      if (isNcPsych) {
        this.handleNcPsychSlotsGate(() => {
          logSheetThenProceed({
            Phone: this.formData.phoneNumber.trim(),
            Email: this.formData.email.trim(),
            State: this.formData.state
          }, () => {
            try { localStorage.setItem('np.fingerprint', fingerprint); } catch {}
          });
        });
      } else {
        logSheetThenProceed({
          Phone: this.formData.phoneNumber.trim(),
          Email: this.formData.email.trim(),
          State: this.formData.state
        }, () => {
          try { localStorage.setItem('np.fingerprint', fingerprint); } catch {}
        });
      }
    } else {
      this.isSubmitting = false;
    }
  }

  private validateForm(): boolean {
    let isValid = true;

    // Validate first name
    if (!this.formData.firstName.trim()) {
      this.formErrors.firstName = 'Please enter your legal first name';
      isValid = false;
    }

    // Validate last name
    if (!this.formData.lastName.trim()) {
      this.formErrors.lastName = 'Please enter your legal last name';
      isValid = false;
    }

    // Validate birth date
    if (!this.formData.birthDate.trim()) {
      this.formErrors.birthDate = 'Please enter your birth date';
      isValid = false;
    } else {
      if (!this.validateDobField()) {
        isValid = false;
      }
      if (!this.validateAge()) {
        isValid = false;
      }
    }

    // Validate birth sex
    if (!this.formData.birthSex) {
      this.formErrors.birthSex = 'Please select your birth sex';
      isValid = false;
    }

    // Validate email
    if (!this.formData.email.trim()) {
      this.formErrors.email = 'Please enter your email address';
      isValid = false;
    } else if (!this.isValidEmail(this.formData.email)) {
      this.formErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Validate phone number
    if (!this.formData.phoneNumber.trim()) {
      this.formErrors.phoneNumber = 'Please enter your phone number';
      isValid = false;
    } else if (!this.isValidPhoneNumber(this.formData.phoneNumber)) {
      this.formErrors.phoneNumber = 'Please enter a valid phone number';
      isValid = false;
    }

    // Validate state
    if (!this.formData.state) {
      this.formErrors.state = 'Please enter the state you are located in';
      isValid = false;
    }

    // Validate treatment type (only if treatment section is visible)
    if (this.shouldShowTreatmentSection() && !this.formData.treatmentType) {
      this.formErrors.treatmentType = 'Please select a treatment type';
      isValid = false;
    }

    const usingInsurance = this.payType !== 'self-pay';
    if (this.shouldShowTreatmentSection() && usingInsurance && !this.providerCarrierId) {
      this.formErrors.coverage = 'Please select an insurance carrier or choose self-pay/no insurance';
      isValid = false;
    }

    return isValid;
  }

  private validateDobField(): boolean {
    const raw = (this.formData.birthDate || '').trim();
    const norm = raw.replace(/\./g, '/').replace(/-/g, '/');
    const m = norm.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) {
      this.formErrors.birthDate = 'Please enter correct DOB';
      return false;
    }
    const month = parseInt(m[1], 10);
    const day = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    if (year < 1900) {
      this.formErrors.birthDate = 'Please enter correct DOB';
      return false;
    }
    if (month < 1 || month > 12) {
      this.formErrors.birthDate = 'Please enter correct DOB';
      return false;
    }
    const dt = new Date(year, month - 1, day);
    if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
      this.formErrors.birthDate = 'Please enter correct DOB';
      return false;
    }
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (dt >= todayOnly) {
      this.formErrors.birthDate = 'Please enter correct DOB';
      return false;
    }
    return true;
  }

  private clearAllErrors(): void {
    this.formErrors = {
      firstName: '',
      lastName: '',
      birthDate: '',
      birthSex: '',
      email: '',
      phoneNumber: '',
      state: '',
      treatmentType: '',
      coverage: ''
    };
  }

  private getCoverageSelection() {
    return {
      payType: this.payType,
      providerCarrierId: this.providerCarrierId,
      providerCarrier: this.providerCarrier
    };
  }

  private syncInsuranceInfoToAuth(): void {
    try {
      this.auth.updatePatientInfo({
        insurance: {
          payType: this.payType || undefined,
          providerCarrier: this.providerCarrier || undefined,
          providerCarrierId: this.providerCarrierId ?? undefined,
          primaryFinancialClassId: this.providerFinancialClassId ?? undefined
        }
      });
    } catch {}
  }

  private async refreshAvailabilityAfterCoverageChange(): Promise<void> {
    await this.maybeCheckAvailability();
    if (this.canSeeSlots && this.formData.state) {
      await this.fetchStateSlots(this.formData.state);
    }
  }

  private loadCarriersFromApiOrStorage(): void {
    void this.refreshCarrierOptionsForCurrentDetails();
  }

  private async refreshCarrierOptionsForCurrentDetails(): Promise<void> {
    if (!this.hasCarrierPrerequisites()) {
      this.carriers = [];
      this.carriersLoaded = true;
      this.syncCarrierSelectionForState();
      this.cdr.detectChanges();
      return;
    }

    await this.allProvidersCache.ensureLoaded();
    const state = this.patientStateForCarriers;
    const uiVisitType = mapVisitTypeCodeToLabel(this.formData.treatmentType) || '';
    const profileIds = this.allProvidersCache.getProfileIdsForState(state, uiVisitType);
    const ageEligibleProfileIds = this.ageLimits.filterProfileIdsByAge(profileIds, this.formData.birthDate, uiVisitType) || [];
    this.carriers = await this.insuranceCoverage.getCarrierOptionsForProfiles(ageEligibleProfileIds);
    this.carriersLoaded = true;
    this.syncCarrierSelectionFromCurrentValue();
    this.syncCarrierSelectionForState();
    this.cdr.detectChanges();
  }

  private syncCarrierSelectionFromCurrentValue(): void {
    if (!this.providerCarrier || this.providerCarrierId != null) return;
    const match = this.carriersForPatientState.find((carrier) => carrier.label === this.providerCarrier);
    if (!match) return;
    this.providerCarrierId = match.id;
    this.providerFinancialClassId = match.financialClassId;
  }

  private syncCarrierSelectionForState(): void {
    if (this.payType === 'self-pay' || this.noInsuranceCardWithMe) return;
    const allowed = this.sortedCarriers;
    const inList = allowed.some((carrier) => carrier.label === this.providerCarrier);
    if (this.providerCarrier && !inList) {
      this.providerCarrier = '';
      this.providerCarrierId = null;
      this.providerFinancialClassId = 'fclass12';
      this.carrierSearch = '';
      this.payType = '';
      this.clearFieldError('coverage');
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 35;
  }

  private isValidPhoneNumber(phone: string): boolean {
    // Check if phone number matches the format 111-111-1111
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  }

  private validateAge(): boolean {
    if (!this.formData.birthDate) {
      return true; // Don't validate age if birth date is empty
    }

    try {
      // Parse the birth date (assuming MM-DD-YYYY format from calendar component)
      const birthDate = new Date(this.formData.birthDate);
      const today = new Date();
      
      // Check if the date is valid
      if (isNaN(birthDate.getTime())) {
        return true; // Let other validation handle invalid date format
      }
      
      // Calculate age in years
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      // Check if age is less than 4
      if (age < 4) {
        this.formErrors.birthDate = 'We serve patients aged 4 and older. For younger patients, please call 919-391-7202.';
        return false;
      }
      
      return true;
    } catch (error) {
      // If there's any error parsing the date, let other validation handle it
      return true;
    }
  }

  // Remove the isSubmitDisabled method since button will always be enabled
  // isSubmitDisabled(): boolean {
  //   return !this.isFormValid();
  // }

  // Persist selected date for slots page to read the same cache key
  private saveDateToCache(dateStr: string): void {
    try {
      localStorage.setItem('availableSlots.selectedDateCache', dateStr);
    } catch {}
  }

  private toStateAbbrev(input: string): string {
    try {
      const text = (input || '').trim();
      if (!text) return '';
      const up = text.toUpperCase();
      if (/^[A-Z]{2}$/.test(up)) return up;
      const found = STATE_VISIT_CONFIG.find(s => s.name.toUpperCase() === up);
      return (found?.code || up).toUpperCase();
    } catch {
      return (input || '').toString().toUpperCase();
    }
  }

  private getSlotsCacheScope(dob?: string): string {
    const age = this.computeAgeForScope((dob || '').toString());
    const ageScope = age === null ? 'age:unknown' : `age:${age}`;
    return `${ageScope}|${this.insuranceCoverage.getCoverageCacheSegment(this.getCoverageSelection())}|${this.partnerProviderContext.getCacheScopeSegment()}`;
  }

  private computeAgeForScope(rawDob: string): number | null {
    try {
      const text = (rawDob || '').toString().trim();
      if (!text) return null;
      let dob: Date | null = null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        const [y, m, d] = text.split('-').map(n => parseInt(n, 10));
        dob = new Date(y, m - 1, d);
      } else {
        const normalized = text.replace(/\./g, '/').replace(/-/g, '/');
        const parts = normalized.split('/');
        if (parts.length === 3) {
          const mm = parseInt(parts[0], 10);
          const dd = parseInt(parts[1], 10);
          const yyyy = parseInt(parts[2], 10);
          dob = new Date(yyyy, mm - 1, dd);
        }
      }
      if (!dob || !isFinite(dob.getTime())) return null;
      const now = new Date();
      let age = now.getFullYear() - dob.getFullYear();
      const monthDiff = now.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age--;
      return Number.isFinite(age) ? age : null;
    } catch {
      return null;
    }
  }

}
